"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Users, Crown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { Dialog, ResponsiveDialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/app/_module/components/ui/dialog";
import { formatDuration } from "@/app/_module/utils/formatters";
import { useAgentWorkload } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";
import { getIndividualUserStats } from "../../services/services";

export interface CompareAgent {
  userId: string;
  name: string;
  agencyName?: string | null;
  avatarUrl?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agencyId: string;
  agents: CompareAgent[];
  period: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
}

type MetricKey = "premium" | "policies" | "dials" | "talk" | "worked" | "closeRate" | "conversion";
interface Metric {
  key: MetricKey;
  label: string;
  hint: string;
  fmt: (n: number) => string;
  /** all-time figures come from the Lead Assignment roster, not the date range */
  allTime?: boolean;
}

const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const int = (n: number) => Math.round(n).toLocaleString("en-US");
const pct = (n: number) => `${n.toFixed(1)}%`;

const METRICS: Metric[] = [
  { key: "premium", label: "Premium sold", hint: "Sold & Approved annual premium in the range", fmt: money },
  { key: "policies", label: "Policies", hint: "Sold & Approved policies in the range", fmt: int },
  { key: "dials", label: "Dials", hint: "Calls placed in the range", fmt: int },
  { key: "talk", label: "Talk time", hint: "Same definition as Agent Details: own calls, sane durations only", fmt: (n) => formatDuration(n) },
  { key: "worked", label: "Leads worked", hint: "Distinct leads touched by any channel — all-time", fmt: int, allTime: true },
  { key: "closeRate", label: "Close rate", hint: "(Sold & Approved + Underwriting) ÷ leads that reached a decision, in the range", fmt: pct },
  { key: "conversion", label: "Conversion", hint: "Assigned leads that produced a policy ÷ assigned leads — all-time", fmt: pct, allTime: true },
];

const initials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";

const PERIOD_LABEL: Record<string, string> = { "6-months": "Last 6 months", "12-months": "Last 12 months", "year-to-date": "Year to date" };

/**
 * Agents as columns, metrics as rows. Every cell carries a bar scaled to the
 * row's best, the leader is marked, and each agent's header counts the rows
 * they lead — the coaching question ("who's strong where?") answered without
 * opening seven profiles. Range metrics use the same per-agent endpoint as
 * Agent Details; the two all-time rows come from the Lead Assignment pass.
 */
export default function AgentCompareDialog({ open, onOpenChange, agencyId, agents, period, startDate, endDate }: Props) {
  const stats = useQueries({
    queries: agents.map((a) => ({
      queryKey: ["agentCompare", agencyId, a.userId, period, startDate ?? "", endDate ?? ""],
      queryFn: () => getIndividualUserStats({ agencyId, userId: a.userId, period, startDate, endDate }),
      enabled: open && !!agencyId,
      staleTime: 60_000,
    })),
  });
  const { data: workload } = useAgentWorkload();
  const workloadById = useMemo(() => {
    const m = new Map<string, any>();
    for (const w of ((workload as any)?.agents || []) as any[]) m.set(w.agentId || w.id, w);
    return m;
  }, [workload]);

  // values[agentIndex][metricKey]; null = not available (owners have no roster row, etc.)
  const values = agents.map((a, i) => {
    const s = stats[i]?.data as any;
    const w = workloadById.get(a.userId);
    const v: Record<MetricKey, number | null> = {
      premium: s ? Number(s.issuedPremium ?? 0) : null,
      policies: s ? Number(s.issuedPolicies ?? 0) : null,
      dials: s ? Number(s.totalCalls ?? 0) : null,
      talk: s ? Number(s.dialTime ?? 0) : null,
      worked: w ? Number(w.workedLeads ?? 0) : null,
      closeRate: s ? Number(s.closingRate ?? 0) : null,
      conversion: w ? Number(w.conversionRate ?? 0) : null,
    };
    return v;
  });
  const loading = stats.some((q) => q.isLoading);

  const rowMax = (key: MetricKey) => Math.max(0, ...values.map((v) => v[key] ?? 0));
  const leaders = (key: MetricKey) => {
    const max = rowMax(key);
    if (max <= 0) return new Set<number>();
    return new Set(values.map((v, i) => (v[key] === max ? i : -1)).filter((i) => i >= 0));
  };
  const winsByAgent = agents.map((_, i) => METRICS.filter((m) => leaders(m.key).has(i) && leaders(m.key).size === 1).length);
  const rowAvg = (key: MetricKey) => {
    const xs = values.map((v) => v[key]).filter((x): x is number => x != null);
    return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null;
  };

  const rangeLabel = startDate && endDate
    ? `${new Date(startDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(endDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
    : PERIOD_LABEL[period] || period;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent size="full" className="p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b text-left">
          <DialogTitle className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base text-left">
            <Users className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="whitespace-nowrap">Compare {agents.length} agents</span>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 whitespace-nowrap">{rangeLabel}</span>
          </DialogTitle>
          {/* Phones get the one line that matters; the full note is for desktop. */}
          <DialogDescription className="text-left sm:hidden">Swipe the table sideways. Best in each row is marked.</DialogDescription>
          <DialogDescription className="hidden sm:block text-left">
            Bars are scaled to the best in each row; the leader is marked. Rows tagged <span className="rounded bg-gray-100 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">all-time</span> come from the Lead Assignment roster and ignore the date range.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-gray-50/60">
                <th className="sticky left-0 z-10 bg-gray-50/60 px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-44">Metric</th>
                {agents.map((a, i) => (
                  <th key={a.userId} className="px-4 py-3 text-left align-top min-w-[11rem]">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8 border border-gray-200">
                        {a.avatarUrl && <AvatarImage src={a.avatarUrl} alt="" />}
                        <AvatarFallback className="text-[11px] font-semibold">{initials(a.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900">{a.name}</div>
                        <div className="truncate text-xs font-normal text-gray-500">{a.agencyName || " "}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs font-medium text-gray-500">
                      {winsByAgent[i] > 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><Crown className="h-3 w-3" /> leads {winsByAgent[i]} of {METRICS.length}</span>
                      ) : (
                        <span className="text-gray-400">leads none</span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-left align-top min-w-[8rem] border-l border-dashed">
                  <div className="text-sm font-semibold text-gray-500">Group avg</div>
                  <div className="text-xs font-normal text-gray-400">{agents.length} agents</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((m) => {
                const max = rowMax(m.key);
                const lead = leaders(m.key);
                const avg = rowAvg(m.key);
                return (
                  <tr key={m.key} className="border-b last:border-b-0 hover:bg-gray-50/40">
                    <th scope="row" className="sticky left-0 z-10 bg-white px-6 py-3 text-left align-middle font-medium text-gray-800" title={m.hint}>
                      <div className="flex items-center gap-2">
                        {m.label}
                        {m.allTime && <span className="rounded bg-gray-100 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">all-time</span>}
                      </div>
                    </th>
                    {agents.map((a, i) => {
                      const v = values[i][m.key];
                      const isLead = lead.has(i) && lead.size < agents.length;
                      const width = v != null && max > 0 ? Math.max((v / max) * 100, v > 0 ? 3 : 0) : 0;
                      return (
                        <td key={a.userId} className="px-4 py-3 align-middle">
                          {loading && v == null ? (
                            <div className="h-9 animate-pulse rounded bg-gray-100" />
                          ) : v == null ? (
                            <span className="text-gray-300" title="Not on the Lead Assignment roster (owners and admins are not)">—</span>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-baseline gap-1.5">
                                <span className={`tabular-nums ${isLead ? "font-semibold text-emerald-700" : "font-medium text-gray-900"}`}>{m.fmt(v)}</span>
                                {isLead && <Crown className="h-3 w-3 text-emerald-600" aria-label="Leads this metric" />}
                              </div>
                              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
                                <div className={`h-full rounded-full transition-[width] ${isLead ? "bg-emerald-500" : "bg-primary/60"}`} style={{ width: `${width}%` }} />
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 align-middle border-l border-dashed">
                      {avg == null ? <span className="text-gray-300">—</span> : <span className="tabular-nums text-gray-500">{m.fmt(m.key === "closeRate" || m.key === "conversion" ? avg : Math.round(avg))}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}
